import type { MediaPlatformImage } from '@wix/members-domain-ts';
import { Camera } from '@wix/wix-ui-icons-common/on-stage';
import { useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import classNames from 'classnames';
import type { FC } from 'react';
import React, { useRef } from 'react';
import { Avatar } from 'wix-ui-tpa/cssVars';

import { acceptableImageExtensions } from '../../../constants/image';
import { getElementId } from '../../../services/html-attributes';
import type { InjectedClassNameProps, Nullable } from '../../../types';
import { DataHook, ElementId, ProfileLayout } from '../../../types';
import styles from './AvatarInput.scss';

const WIX_MEDIA_INDICATOR = 'static.wixstatic.com/media';
const WIX_MP_INDICATOR = 'images-wixmp';

type AvatarProps = {
  url: Nullable<string | MediaPlatformImage | undefined>;
  imageSize: number;
  profileLayout?: ProfileLayout;
  canUpload?: boolean;
  onClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  name?: string;
  isMobile?: boolean;
} & InjectedClassNameProps;

const shouldOptimizeImage = (url: string) => {
  return (
    !url.includes('v1/fill') &&
    (url.includes(WIX_MEDIA_INDICATOR) || url.includes(WIX_MP_INDICATOR))
  );
};

const toOptimizedImage = (url: string, imageSize: number) => {
  if (shouldOptimizeImage(url)) {
    return `${url}/v1/fill/w_${imageSize},h_${imageSize},q_80/file.webp`;
  }

  return url;
};

const AvatarInput: FC<AvatarProps> = ({
  url,
  className = '',
  name,
  onClick,
  canUpload,
  profileLayout,
  imageSize,
  isMobile = false,
}) => {
  const { t } = useTranslation();
  const { widgetId } = useEnvironment();
  const inputRef = useRef<HTMLInputElement>(null);

  const inputId = getElementId(widgetId, ElementId.ProfilePhotoInput);
  const imageURL = url
    ? typeof url === 'object'
      ? url?.file_name
      : toOptimizedImage(url, imageSize)
    : undefined;

  const wrapperClasses = {
    [styles.wrapper]: true,
    [styles.onHover]: canUpload,
    [styles.fullWidth]: profileLayout === ProfileLayout.FullWidth,
    [styles.card]: profileLayout === ProfileLayout.Card,
    [styles.mobile]: isMobile,
  };

  const ariaLabel = imageURL
    ? t('profile-widget.change-profile-photo')
    : t('profile-widget.add-profile-photo.aria-label');

  const handleAvatarKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!canUpload) {
      return;
    }

    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      inputRef.current?.click();
    }
  };

  const handleAvatarClick = () => {
    if (!canUpload) {
      return;
    }

    inputRef.current?.click();
  };

  return (
    <div className={styles.container}>
      {canUpload && (
        <input
          id={inputId}
          className={styles.uploadInput}
          type="file"
          accept={acceptableImageExtensions}
          onChange={onClick}
          aria-label={ariaLabel}
          ref={inputRef}
          tabIndex={-1}
        />
      )}
      <div
        className={classNames(wrapperClasses, className)}
        aria-label={ariaLabel}
        onKeyDown={handleAvatarKeyDown}
        onClick={handleAvatarClick}
        tabIndex={canUpload ? 0 : -1}
      >
        <Avatar
          name={name}
          src={imageURL}
          data-hook={DataHook.ProfilePhoto}
          className={styles.avatar}
        />
        {!isMobile && (
          <div className={styles.pictureWrapper}>
            <div className={styles.iconOval}>
              <Camera aria-hidden="true" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AvatarInput;
